import { Helmet } from "react-helmet-async";
import { PADMORA_DEFAULT_SEO_DESCRIPTION, PADMORA_DEFAULT_SEO_TITLE } from "@/lib/constants";

type SEOProps = {
  title?: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  image?: string;
  /** e.g. https://padmora.com */
  siteUrl?: string;
  /** Defaults to "Padmora" */
  siteName?: string;
  /** For article pages */
  type?: "website" | "article" | "profile";
  /** Keywords for better SEO */
  keywords?: string[];
  /** Author for article pages */
  author?: string;
  /** Publish date for article pages */
  publishedTime?: string;
  /** Modified date for article pages */
  modifiedTime?: string;
  /** Section/category for article pages */
  section?: string;
  /** For breadcrumb navigation */
  breadcrumbs?: Array<{ name: string; url: string }>;
  /** JSON-LD structured data */
  structuredData?: object;
  /** Custom meta tags */
  customMeta?: Array<{ name?: string; property?: string; content: string }>;
  /** AI-specific optimization */
  aiOptimization?: {
    /** AI search keywords */
    aiKeywords?: string[];
    /** Semantic entities */
    entities?: string[];
    /** Content topics */
    topics?: string[];
  };
  /** Performance optimization */
  performance?: {
    /** Preload critical resources */
    preload?: string[];
    /** DNS prefetch domains */
    dnsPrefetch?: string[];
    /** Preconnect domains */
    preconnect?: string[];
  };
};

/** Aligned with index.html defaults; Worker replaces these in HTML for document routes. */
const DEFAULTS = {
  title: PADMORA_DEFAULT_SEO_TITLE,
  description: PADMORA_DEFAULT_SEO_DESCRIPTION,
  siteName: "Padmora",
  image: "/android-chrome-512x512.png",
  siteUrl: "https://padmora.com",
  type: "website" as const,
  keywords: [
    "commodity trading",
    "letter of credit",
    "FOB to CFR",
    "ammonium sulphate",
    "AI trading platform",
    "US trading firm",
    "UCP600 compliant",
    "commodity finance",
    "trade finance",
    "supply chain automation"
  ],
  aiOptimization: {
    aiKeywords: [
      "artificial intelligence trading",
      "machine learning commodity trading",
      "automated trade execution",
      "predictive analytics commodities",
      "AI compliance screening"
    ],
    entities: [
      "United States",
      "Ammonium Sulphate",
      "Letter of Credit",
      "UCP600",
      "Trade Finance"
    ],
    topics: [
      "Commodity Trading",
      "Trade Finance",
      "Supply Chain Management",
      "AI Automation",
      "Compliance Management"
    ]
  }
};

// Default structured data for organization
const DEFAULT_STRUCTURED_DATA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Padmora",
  "description": "US boutique trading firm specializing in AI-powered commodity deals with LC-backed financing and full compliance automation.",
  "url": "https://padmora.com",
  "logo": "https://padmora.com/android-chrome-512x512.png",
  "foundingDate": "2025",
  "founders": [
    {
      "@type": "Person",
      "name": "Michel Padron Morales"
    }
  ],
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "US",
    "addressRegion": "Wyoming"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": ["English"],
    "email": "hello@padmora.com"
  },
  "sameAs": [
    "https://www.linkedin.com/company/padmora"
  ],
  "knowsAbout": [
    "Commodity Trading",
    "Trade Finance",
    "AI Automation",
    "Supply Chain Management",
    "Letter of Credit",
    "UCP600 Compliance"
  ]
};

// Helper function to sanitize titles for Twitter (safe if title is undefined)
const sanitizeTwitterTitle = (title: string | undefined | null): string => {
  return (title ?? '')
    .replace(/[""]/g, '"') // Replace smart quotes
    .replace(/['']/g, "'") // Replace smart apostrophes  
    .replace(/—/g, "-") // Replace em dash
    .replace(/–/g, "-") // Replace en dash
    .replace(/&/g, "and") // Replace ampersand
    .replace(/<[^>]*>/g, "") // Remove HTML tags
    .trim()
    .substring(0, 70); // Twitter limit
};

const sanitizeTwitterDescription = (description: string | undefined | null): string => {
  return (description ?? '')
    .replace(/[""]/g, '"')
    .replace(/['']/g, "'")
    .replace(/—/g, "-")
    .replace(/–/g, "-")
    .replace(/&/g, "and")
    .replace(/<[^>]*>/g, "")
    .trim()
    .substring(0, 200); // Twitter limit
};

export function SEO(props: SEOProps) {
  const title = props.title ?? DEFAULTS.title;
  const description = props.description ?? DEFAULTS.description;
  const siteName = props.siteName ?? DEFAULTS.siteName;
  const siteUrl = props.siteUrl ?? DEFAULTS.siteUrl;
  const imagePath = props.image ?? DEFAULTS.image;
  const image = imagePath.startsWith("http") ? imagePath : `${siteUrl}${imagePath}`;
  const canonical = props.canonical ?? siteUrl;
  const type = props.type ?? DEFAULTS.type;
  const robots = props.noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";

  // Sanitize for Twitter
  const twitterTitle = sanitizeTwitterTitle(title);
  const twitterDescription = sanitizeTwitterDescription(description);

  // Combine default and custom keywords
  const keywords = [...(DEFAULTS.keywords || []), ...(props.keywords || [])];
  const aiOptimization = { ...DEFAULTS.aiOptimization, ...props.aiOptimization };
  const performance = props.performance || {};

  // Generate breadcrumb structured data
  const breadcrumbStructuredData = props.breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": props.breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  } : null;

  // Article structured data for article pages
  const articleStructuredData = type === "article" ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": image,
    "author": {
      "@type": "Organization",
      "name": props.author || siteName
    },
    "publisher": {
      "@type": "Organization",
      "name": siteName,
      "logo": {
        "@type": "ImageObject",
        "url": image
      }
    },
    "datePublished": props.publishedTime,
    "dateModified": props.modifiedTime,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonical
    },
    "articleSection": props.section,
    "keywords": keywords.join(", ")
  } : null;


  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />
      <meta name="robots" content={robots} />
      <meta name="author" content={props.author || "Padmora Team"} />
      <meta name="language" content="en-US" />
      <meta name="geo.region" content="US-WY" />
      <meta name="geo.placename" content="United States" />

      {/* AI-Specific Meta Tags */}
      <meta name="ai-optimized" content="true" />
      <meta name="ai-keywords" content={aiOptimization.aiKeywords?.join(", ") || ""} />
      <meta name="ai-entities" content={aiOptimization.entities?.join(", ") || ""} />
      <meta name="ai-topics" content={aiOptimization.topics?.join(", ") || ""} />

      {/* Canonical and Alternate Links */}
      <link rel="canonical" href={canonical} />
      <link rel="alternate" href={canonical} hrefLang="en" />

      {/* Open Graph Enhanced */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${siteName} - ${title}`} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Enhanced */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@padmora" />
      <meta name="twitter:creator" content="@padmora" />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${siteName} - ${twitterTitle}`} />
      <meta name="twitter:domain" content="padmora.com" />
      <meta name="twitter:url" content={canonical} />

      {/* Article Meta Tags */}
      {type === "article" && (
        <>
          <meta property="article:author" content={props.author || siteName} />
          {props.publishedTime && <meta property="article:published_time" content={props.publishedTime} />}
          {props.modifiedTime && <meta property="article:modified_time" content={props.modifiedTime} />}
          {props.section && <meta property="article:section" content={props.section} />}
          <meta property="article:tag" content={keywords.join(", ")} />
        </>
      )}

      {/* Performance Optimization */}
      {performance.preload?.map((url) => (
        <link key={url} rel="preload" href={url} as="image" />
      ))}
      {performance.dnsPrefetch?.map((domain) => (
        <link key={domain} rel="dns-prefetch" href={`//${domain}`} />
      ))}
      {performance.preconnect?.map((domain) => (
        <link key={domain} rel="preconnect" href={`//${domain}`} crossOrigin="" />
      ))}

      {/* Custom Meta Tags */}
      {props.customMeta?.map((meta, index) => (
        <meta
          key={index}
          {...(meta.name && { name: meta.name })}
          {...(meta.property && { property: meta.property })}
          content={meta.content}
        />
      ))}

      {/* Favicon and App Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/site.webmanifest" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(props.structuredData || DEFAULT_STRUCTURED_DATA)}
      </script>

      {breadcrumbStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
      )}

      {articleStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData)}
        </script>
      )}

    </Helmet>
  );
}

export default SEO;

